import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@/components/modal/Modal';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { ModalProps } from '@/components/modal/useModal';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { EmailField } from '@/components/form/fields/text/EmailInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { DateField } from '@/components/form/fields/date/DateInput';
import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { AddressRequest } from '@/services/api/contracts/address';
import { Card } from '@/components/sections/Card';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { toast } from 'sonner';

const CheckoutFormSchema = z.object({
  userName: z.string().min(1, 'Required'),
  userEmail: z.string().email('Invalid email').or(z.literal('na')),
  noCorporateEmail: z.boolean().default(false),
  address: AddressRequest.omit({ name: true }),
  deliveryContactName: z.string().min(1, 'Required'),
  deliveryContactEmail: z.string().email('Invalid email'),
  deliveryContactMobile: z.string().min(1, 'Required'),
  customerReference: z.string().optional(),
  targetDate: z.date({ required_error: 'Required By Date is strictly required' }),
  notes: z.string().optional()
});

type CheckoutFormFields = z.infer<typeof CheckoutFormSchema>;

export interface TechRefreshModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  checkoutPayload: {
    hardwareSelections: Record<string, number>;
    inferredServices: Array<{ rateCard: any; quantity: number; labels: string[] }>;
    runningTotal: number;
    devicesToBeCollected: string[];
    fullName?: string;
    workEmail?: string;
    
    // Deployment specific
    cart: Record<string, number>;

  } | null;
  onSuccessCallback: () => void;
}

export const TechRefreshModal: React.FC<TechRefreshModalProps> = ({ 
  mspId, 
  customerId, 
  checkoutPayload, 
  onSuccessCallback, 
  ...props 
}) => {
  const createServiceRequest = useCreateServiceRequest();
  const user = useUser();
  const [partner] = useMspDetailsForCustomer(mspId, customerId);
  const [company] = useCompany(mspId, customerId);
  
  const initialUser = checkoutPayload?.fullName || '';
  const initialEmail = checkoutPayload?.workEmail || '';
  
  const methods = useForm<CheckoutFormFields>({
    resolver: zodResolver(CheckoutFormSchema),
    defaultValues: {
      userName: initialUser,
      userEmail: initialEmail,
      noCorporateEmail: false,
      deliveryContactName: '',
      deliveryContactEmail: '',
      deliveryContactMobile: '',
      customerReference: '',
      targetDate: undefined as any,
      notes: ''
    }
  });

  useEffect(() => {
    if (props.isOpen) {
      methods.reset({
        userName: initialUser,
        userEmail: initialEmail,
        noCorporateEmail: false,
        deliveryContactName: '',
        deliveryContactEmail: '',
        deliveryContactMobile: '',
        customerReference: '',
        targetDate: undefined as any,
        notes: ''
      });
    }
  }, [props.isOpen, initialUser, initialEmail, methods]);

  const handleAddressChange = React.useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const noCorporateEmail = methods.watch('noCorporateEmail');

  useEffect(() => {
    if (noCorporateEmail) {
      methods.setValue('userEmail', 'na', { shouldValidate: true, shouldDirty: true });
    } else if (methods.getValues('userEmail') === 'na') {
      methods.setValue('userEmail', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [noCorporateEmail, methods]);

  const onSubmit = async (data: CheckoutFormFields) => {
    if (!checkoutPayload) return;

    const finalCustomerReference = data.customerReference?.trim() || data.userName || 'No Reference Provided';

    const logisticsDetails = {
      ...data,
      customerReference: finalCustomerReference,
      address: {
        ...data.address,
        name: 'Tech Refresh Address'
      },
      requiredByDate: new Date(data.targetDate).toISOString()
    };

    const details = {
      requestType: 'TechRefresh',
      requestedBy: user?.email || 'Unknown',
      mspId,
      mspName: partner?.name || '',
      customerId,
      customerName: company?.name || '',
      raisedDateTime: new Date().toISOString(),
      customerReference: finalCustomerReference,
      submissionSource: 'DaaS Portal UI',
      totalEstimatedCost: checkoutPayload.runningTotal,
      haloClientId: company?.haloClientId || null,
      haloGeneralUserId: company?.haloGeneralUserId || null
    };

    const finalPayload = {
      details,
      logisticsDetails,
      // Collection payload
      collectedItems: {
        hardwareSelections: checkoutPayload.hardwareSelections,
        devicesToBeCollected: checkoutPayload.devicesToBeCollected
      },
      // Deployment payload
      deployedItems: {
        cart: checkoutPayload.cart
      },
      // Unified Billing
      inferredServices: checkoutPayload.inferredServices.map((s: any) => ({ 
        rateCardId: s.rateCard?.id, 
        name: s.name || s.rateCard?.name,
        quantity: s.quantity,
        price: s.rateCard?.price?.price
      })),
      totalCost: checkoutPayload.runningTotal
    };
    
    try {
      await createServiceRequest.mutateAsync({
        params: { mspId, customerId },
        body: {
          requestedBy: user?.email || 'Unknown',
          requestType: 'TechRefresh',
          customerReference: finalCustomerReference,
          payload: finalPayload
        }
      });
      
      toast.success('Tech Refresh Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  if (!checkoutPayload) return null;

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Tech Refresh</h1>
          </Modal.Controls>
          <p className="text-gray-600">Please provide the logistics details for collecting the old devices and deploying the new ones.</p>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Left Column: The Receipt */}
              <div className="space-y-6">
                <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                  <h3 className="text-lg font-bold border-b pb-3 mb-4">Refresh Summary</h3>
                  
                  {checkoutPayload.devicesToBeCollected.length > 0 && (
                    <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                      <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Collect:</p>
                      <ul className="space-y-1 text-gray-600 list-disc list-inside">
                        {checkoutPayload.devicesToBeCollected.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-4 flex-1 mt-4">
                    {checkoutPayload.inferredServices.map((item: any, index: number) => {
                      const isInventoryItem = item.isInventoryItem;
                      return (
                        <div key={index} className="flex justify-between items-start text-sm border-b border-gray-100 last:border-b-0 pb-2">
                          <div className="pr-4">
                            <span className="font-medium">{item.quantity}x</span> {item.name || item.rateCard.name}
                            {item.hasLifecyclePack && (
                              <span className="italic text-green-600 text-xs block mt-1">Legacy Lifecycle Pack Detected - Cost May Not Apply</span>
                            )}
                          </div>
                          <div className="font-medium whitespace-nowrap">
                            {isInventoryItem ? (
                              <span className="italic">From Inv</span>
                            ) : (
                              new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(((item.rateCard.price?.price || 0) / 100) * item.quantity)
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {checkoutPayload.inferredServices.length === 0 && (
                      <div className="text-gray-500 italic text-sm">No items selected.</div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                    <span>Estimated Total</span>
                    <span>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(checkoutPayload.runningTotal)}</span>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4 bg-white">
                  <div>
                    <TextField id="customerReference" label="Customer Reference" placeholder="This will appear on your invoice..." optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4 bg-white">
                  <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
                </div>
              </div>

              {/* Right Column: Logistics & Data Entry */}
              <div className="space-y-6">

                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">User Details</h4>
                  <TextField id="userName" label="User Name" />
                  <div className={noCorporateEmail ? 'opacity-50 pointer-events-none' : ''}>
                    <TextField id="userEmail" label="User's Corporate Email" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" {...methods.register('noCorporateEmail')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    No Corporate Email
                  </label>
                </div>
                
                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery & Collection Address</h4>
                  <AddressBookSelector 
                    mspId={mspId}
                    customerId={customerId}
                    onChange={handleAddressChange}
                  />
                  {methods.formState.errors.address && (
                    <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                  )}
                </div>

                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery & Collection Contact</h4>
                  <TextField id="deliveryContactName" label="Contact Name" />
                  <EmailField id="deliveryContactEmail" label="Contact Email" />
                  <TextField id="deliveryContactMobile" label="Contact Mobile" />
                </div>

                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Schedule</h4>
                  <DateField id="targetDate" label="Required By Date" />
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={createServiceRequest.isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={createServiceRequest.isLoading}
            >
              {createServiceRequest.isLoading ? 'Submitting...' : 'Confirm Tech Refresh & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
